import { Component, ComponentFactoryResolver, Inject, Injector, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { ILocationService } from "angular";
import { MenuItem } from 'primeng/api';
import { DialogService } from "primeng/dynamicdialog";
import { LogService } from "../../logs/log.service";
import { ReportBuilderStarterComponent } from "../../reports/report-builder-starter.component";
import { AppStateService } from "../services/app-state.service";
import { ShiftsService } from "ng/src/app/shifts/shifts.service";
import { combineLatest, of, Subject } from "rxjs";
import { TieredMenu } from "primeng/tieredmenu";
import { Template, TemplateTypeId, TemplateWithUsers } from "ng/src/app/templates/shared/template.model";
import { PersistentDialogService } from "ng/src/app/shared/persistent-dialog/persistent-dialog.service";
import { DraftNotesComponent } from "ng/src/app/notes/draft-notes/draft-notes.component";
import { NoteControlService } from "ng/src/app/notes/note-controller/note-control.service";
import { NoteService } from "ng/src/app/notes/shared/note.service";
import { BaseProjections, TemplateProjections } from "app/shared/model/request-parameter.model";
import { filter, map, switchMap, takeUntil } from "rxjs/operators";
import { TemplateService } from "ng/src/app/templates/shared/template.service";

@Component({
	selector: 'lb-navbar-fab',
	templateUrl: './navbar-fab.component.html',
	styleUrls: ['./navbar-fab.component.css'],
	providers: [DialogService]
})
export class NavbarFabComponent implements OnInit, OnDestroy {
	@ViewChild(TieredMenu) fabMenu!: TieredMenu;
	@ViewChild('draftPickTemplate') template!: TemplateRef<any>;
	@ViewChild('draftPickHeaderTemplate') headerTemplate!: TemplateRef<any>;
	@ViewChild('draftContainer', { read: ViewContainerRef} ) draftContainer!: ViewContainerRef;
	
	proposedTemplate?: Template;
	private readonly navigationSubject = new Subject<Template | TemplateWithUsers | null>();
	
	private readonly destroySubject = new Subject();
	readonly destroy$ = this.destroySubject.asObservable();
	
	constructor(
		@Inject('$location') private $location: ILocationService,
		private injector: Injector,
		private resolver: ComponentFactoryResolver,
		private dialogService: DialogService,
		private shiftService: ShiftsService,
		private templateService: TemplateService,
		public lbDialogService: PersistentDialogService,
		private noteControllerService: NoteControlService
	) { }

	
	get isActive() {
		return !!this.fabMenu?.visible;
	}
	
	private readonly enterNotesMenuItem: MenuItem & { items?: Array<MenuItem & { template: Template }>} = {
		label: 'Enter Notes',
		icon: 'pi pi-fw pi-file',
		title: "Create a new note using the log's default template",
		command: () => {
			if (!LogService.selectedLog?.defaultTemplate) return;
			this.navigationSubject.next(LogService.selectedLog?.defaultTemplate);
		}
	}

	writeAccessItems: MenuItem[] = [
		this.enterNotesMenuItem,
		{
			label: 'Report',
			icon: 'pi pi-fw pi-folder-open',
			title: 'Create a new report',
			command: () => {
				this.closeFabMenu();

				this.dialogService.open(ReportBuilderStarterComponent, {
					width: '100%',
					height: '100%',
					styleClass: 'report-starter-dialog',
					transitionOptions: '0s' /* Override default animation to not interfere with bootstrap modal */
				});
			}
		},
		{
			label: 'Reminder',
			icon: 'pi pi-fw pi-bell',
			title: 'Create a new reminder',
			command: () => this.navigateTo('/reminder-detail')
		}
	];

	readOnlyAccessItems = [
		this.writeAccessItems.find(mi => mi.label == 'Report')!
	];

	menuItems!: MenuItem[];

	navigateTo(url: string) {
		this.closeFabMenu();
		
		this.$location.url(url);
	}
	
	private get noteSubMenuItems() {
		return this.enterNotesMenuItem.items;
	}

	private set noteSubMenuItems(menuItems: Array<MenuItem & { template: Template}> | undefined) {
		this.enterNotesMenuItem.items = menuItems;
	}

	ngOnInit() {
		combineLatest(
			[
				LogService.onTemplatesChanged$,
				this.shiftService.onShift$,
			]
		).pipe(
			takeUntil(this.destroy$)
		)
		.subscribe(([templates, onShift]) => {
			if (LogService.isReadOnlyUser) {
				this.menuItems = this.readOnlyAccessItems;
				return;
			}

			this.menuItems = this.writeAccessItems;
			
			const user = AppStateService.currentUser;
			templates = templates.filter(t => {
				return t.enterNotes && (t.allowAllUsers || user && t.allowedUsers.some(u => u.user.id == user?.id));
			});

			this.noteSubMenuItems = templates.length == 0 ? undefined :
				templates.map(t => ({
					label: t.name,
					title: `Create a new ${t.name} note`,
					template: t,
					command: () => this.navigationSubject.next(t)
				}));
			
			this.enterNotesMenuItem.disabled = this.noteSubMenuItems == null;
		});

		AppStateService.closeFab$.subscribe(($event?: any) => {
			this.closeFabMenu($event);
		});
		
		this.navigationSubject.pipe(
			takeUntil(this.destroy$)
		)
			.pipe(
				switchMap(template => {
					if (!template)
						return of(null)
					else if ('allowedUsers' in template)
						return of(template);
					
					return this.templateService.getTemplateById(template.id, TemplateProjections.WITH_USERS);
				}),
				map(template => {
					return template && TemplateWithUsers.currentUserCanAccess(template) ? template : null;
				}),
				filter((template) => template != null),
				switchMap(template => {
					const result: { template: TemplateWithUsers | null, hasDrafts: boolean } = {
						template: template,
						hasDrafts: false
					}
					if (!template) {
						return of(result);
					}
					return this.templateHasDrafts(template).pipe(
						map(hasDrafts => {
							result.hasDrafts = hasDrafts;
							return result;
						})
					)
				})
			)
			.subscribe(({ template, hasDrafts }) => {
				if (template && hasDrafts) {
					this.proposedTemplate = template;
					this.lbDialogService.next({
						template: this.template,
						settings: {
							header: this.headerTemplate,
							position: 'top'
						}
					});
					
					return;
				}
				this.navigateToNote(template);
			});
	}
	
	ngOnDestroy(): void {
		this.destroySubject.next();
	}
	
	closeFabMenu($event?: Event) {
		const isRelated = !!$event?.composedPath?.().find(e => (e as HTMLElement).nodeName.toLowerCase() === 'navbar');
		if (isRelated) {
			return;
		}
		this.fabMenu.hide();
	}

	onFabMouseover(event: MouseEvent) {
		// Necessary to avoid NG0100: ExpressionChangedAfterItHasBeenCheckedError when mousing over too quickly in succession
		// See https://github.com/primefaces/primeng/issues/8839
		if (!this.fabMenu.target && !this.fabMenu.visible)
			this.fabMenu.show(event);
	}
	
	navigateToNote(template: TemplateWithUsers | Template | null) {
		this.lbDialogService.close();
		this.noteControllerService.start(template);
	}
	
	createDraftContainer(container: ViewContainerRef) {
		const component = container.createComponent(this.resolver.resolveComponentFactory(DraftNotesComponent));
		component.instance.filterTemplateId = this.proposedTemplate?.id ?? null;
		component.instance.collapseLargeSection = false;
		component.instance.noteEditStarted.pipe(
			takeUntil(component.instance.destroy$)
		)
			.subscribe(() => this.lbDialogService.close());
	}
	
	private templateHasDrafts(template: Template) {
		if (template.templateType.id === TemplateTypeId.OPERATIONAL) return of(false);
		
		return this.injector.get(NoteService).getLogNotes({
			template: template.id,
			draft: true,
			size: 1,
			projection: BaseProjections.IdProjection,
			'coCreators.user': LogService.selectedLog!.activeLogUser.user.id
		})
			.pipe(
				map((drafts) => drafts.length > 0)
			)
	}
}